// =============================================================================
// PEDRA — src/optimizer/index.ts
// Ponto de entrada do nucleo de otimizacao.
//
//   optimize(input, onProgress?, shouldStop?) -> OptimizationResult
//
// Contrato: sincrona, bloqueante, NUNCA lanca, DETERMINISTA (mesma entrada +
// mesma semente = mesmo resultado). `performance.now()` so aparece para medir
// tempo e respeitar `settings.timeBudgetMs` — nunca para gerar aleatoriedade.
//
// Estrategia de busca (meta-heuristica de 3 fases, sempre com prefixo fixo):
//   Fase A  nucleo deterministico: 24 combinacoes (ordenacao x regra de encaixe
//           x regra de corte) escolhidas a dedo, sempre nesta ordem;
//   Fase B  varredura sistematica do produto cartesiano restante;
//   Fase C  reinicios perturbados com o PRNG semeado por `settings.seed`.
// O numero de tentativas e derivado do orcamento e do tamanho do problema de
// forma DETERMINISTICA — o relogio so serve de valvula de seguranca.
//
// CONTRATO DE TEMPO (`settings.timeBudgetMs`): o orcamento limita a BUSCA, nao
// o calculo inteiro. A PRIMEIRA tentativa sempre roda ate o fim (sem ela nao
// existe plano nenhum) e a montagem final do relatorio tambem nao e cortada;
// num caso patologico — 2000 pecas de uma por chapa — esse piso sozinho ja
// custa ~100 ms. Da segunda tentativa em diante o corte e o ORCAMENTO DE
// TRABALHO deterministico (ver WORK_UNITS_PER_MS), medido em pecas varridas e
// nao em milissegundos, para que o plano continue igual em qualquer maquina.
// =============================================================================

import type {
  CutAxis,
  Micron2,
  OptimizationInput,
  OptimizationPhase,
  OptimizationPriority,
  OptimizationResult,
  Part,
  ProgressEvent,
  Rect,
  Settings,
  SheetLayout,
  StockSheet,
  UnfitPart,
  UnfitReason,
} from '../core/types';
import type { FitRule, PackItem, PackedSheet, SplitRule } from './guillotine';
import { collectFreeRects, orientationsFor, packSheetGuillotine } from './guillotine';
import { packSheetMaxRects } from './maxrects';
import { collectOffcuts, extractCuts, reconstructGuillotine } from './cuts';
import type { StockEntry } from './packer';
import {
  bandThicknessMap,
  buildStockPool,
  computeStats,
  groupByMaterial,
  makeUnfit,
  mergeUnfit,
  preparePart,
  resetStockPool,
  stackIdenticalSheets,
} from './packer';
import type { Rng } from './rng';
import { hash32, mulberry32, randInt, shuffle } from './rng';

// -----------------------------------------------------------------------------
// LIMITES E PADROES
// -----------------------------------------------------------------------------

/** Teto absoluto de chapas num resultado. Acima disso o resto vira `unfit`. */
const MAX_SHEETS = 2_000;
/** Intervalo minimo entre eventos de progresso. */
const PROGRESS_INTERVAL_MS = 100;
/** Acima deste numero de pecas o MaxRects sai da busca (reconstrucao cara). */
const MAXRECTS_LIMIT = 120;

/**
 * ORCAMENTO DE TRABALHO — a forma DETERMINISTICA de respeitar `timeBudgetMs`.
 *
 * Contar so tentativas nao basta: o custo de uma tentativa e
 * O(pecas x chapas abertas), e num caso patologico (uma peca por chapa, 2000
 * copias) uma unica tentativa ja passa de 100 ms — o orcamento estourava 12x.
 * Medir o relogio dentro dos lacos quentes resolveria o tempo mas tornaria o
 * PLANO dependente da maquina, o que e inaceitavel.
 *
 * Escolha: contar TRABALHO em unidades deterministicas (uma unidade = uma peca
 * varrida por um empacotamento de chapa) e derivar o teto do orcamento. Mesma
 * entrada => mesmo numero de unidades => mesmo plano, em qualquer maquina.
 * A constante abaixo e a taxa medida neste nucleo (~20 mil pecas varridas por
 * milissegundo); ela so precisa estar na ordem de grandeza certa.
 */
const WORK_UNITS_PER_MS = 20_000;

const FALLBACK_SETTINGS: Settings = {
  kerf: 4_000,
  considerGrain: false,
  considerMaterial: true,
  edgeBandingEnabled: false,
  labelsOnPanels: true,
  useSingleStockUnit: false,
  unit: 'mm',
  dimensionOrder: 'lengthFirst',
  decimalPlaces: 1,
  minTrimDimension: 0,
  stackSamePanels: 'auto',
  priority: 'leastWaste',
  cutOrientation: 'optimal',
  timeBudgetMs: 8_000,
  seed: 20260730,
};

/** Relogio monotonico. Nunca `Date.now()`. Ausente = 0 (busca fica so por iteracoes). */
function nowMs(): number {
  const perf = typeof performance !== 'undefined' ? performance : undefined;
  return perf !== undefined && typeof perf.now === 'function' ? perf.now() : 0;
}

function clampNumber(v: number, lo: number, hi: number, fallback: number): number {
  if (!Number.isFinite(v)) return fallback;
  return v < lo ? lo : v > hi ? hi : v;
}

/** Preenche buracos e corrige valores absurdos sem nunca lancar. */
function normalizeSettings(raw: Settings | undefined | null): Settings {
  const s = raw !== undefined && raw !== null && typeof raw === 'object' ? raw : FALLBACK_SETTINGS;
  return {
    ...FALLBACK_SETTINGS,
    ...s,
    kerf: clampNumber(s.kerf, 0, 50_000, FALLBACK_SETTINGS.kerf),
    minTrimDimension: clampNumber(s.minTrimDimension, 0, 100_000_000, 0),
    decimalPlaces: Math.round(clampNumber(s.decimalPlaces, 0, 3, 1)),
    timeBudgetMs: clampNumber(s.timeBudgetMs, 50, 600_000, FALLBACK_SETTINGS.timeBudgetMs),
    seed: Number.isFinite(s.seed) ? Math.trunc(s.seed) : FALLBACK_SETTINGS.seed,
  };
}

// -----------------------------------------------------------------------------
// ORDENACOES
// -----------------------------------------------------------------------------

type OrderingRule =
  | 'areaDesc'
  | 'areaAsc'
  | 'maxSideDesc'
  | 'minSideDesc'
  | 'perimeterDesc'
  | 'lengthDesc'
  | 'widthDesc'
  | 'diffDesc';

const ORDERINGS: readonly OrderingRule[] = [
  'areaDesc',
  'maxSideDesc',
  'lengthDesc',
  'widthDesc',
  'perimeterDesc',
  'minSideDesc',
  'diffDesc',
  'areaAsc',
];

function primaryDelta(rule: OrderingRule, a: PackItem, b: PackItem): number {
  switch (rule) {
    case 'areaDesc':
      return b.width * b.length - a.width * a.length;
    case 'areaAsc':
      return a.width * a.length - b.width * b.length;
    case 'maxSideDesc':
      return Math.max(b.width, b.length) - Math.max(a.width, a.length);
    case 'minSideDesc':
      return Math.min(b.width, b.length) - Math.min(a.width, a.length);
    case 'perimeterDesc':
      return b.width + b.length - (a.width + a.length);
    case 'lengthDesc':
      return b.length - a.length;
    case 'widthDesc':
      return b.width - a.width;
    case 'diffDesc':
      return Math.abs(b.width - b.length) - Math.abs(a.width - a.length);
  }
}

/**
 * Comparador com desempate CANONICO: o resultado nao depende da ordem em que o
 * usuario digitou as linhas.
 */
function compareItems(rule: OrderingRule, a: PackItem, b: PackItem): number {
  const d = primaryDelta(rule, a, b);
  if (d !== 0) return d < 0 ? -1 : 1;
  if (a.width !== b.width) return b.width - a.width;
  if (a.length !== b.length) return b.length - a.length;
  if (a.orientation !== b.orientation) return a.orientation - b.orientation;
  if (a.partId !== b.partId) return a.partId < b.partId ? -1 : 1;
  return a.instance - b.instance;
}

// -----------------------------------------------------------------------------
// COMBINACOES
// -----------------------------------------------------------------------------

interface Combo {
  ordering: OrderingRule;
  fit: FitRule;
  split: SplitRule;
  firstAxis: CutAxis | null;
  engine: 'guillotine' | 'maxrects';
}

const FITS: readonly FitRule[] = [
  'bestArea',
  'bestShortSide',
  'exactFit',
  'bestLongSide',
  'bottomLeft',
];

const SPLITS: readonly SplitRule[] = ['maxArea', 'minArea', 'shorterAxis', 'longerAxis'];

/** Prefixo fixo: as 24 combinacoes com melhor historico. Sempre nesta ordem. */
const PHASE_A: ReadonlyArray<readonly [OrderingRule, FitRule, SplitRule]> = [
  ['areaDesc', 'bestArea', 'maxArea'],
  ['areaDesc', 'bestShortSide', 'shorterAxis'],
  ['lengthDesc', 'exactFit', 'minArea'],
  ['maxSideDesc', 'bestArea', 'maxArea'],
  ['widthDesc', 'exactFit', 'minArea'],
  ['areaDesc', 'bestShortSide', 'minArea'],
  ['perimeterDesc', 'bestArea', 'maxArea'],
  ['areaDesc', 'bottomLeft', 'shorterAxis'],
  ['maxSideDesc', 'bestShortSide', 'shorterAxis'],
  ['lengthDesc', 'bestArea', 'longerAxis'],
  ['widthDesc', 'bestArea', 'longerAxis'],
  ['areaDesc', 'bestLongSide', 'maxArea'],
  ['perimeterDesc', 'bestShortSide', 'minArea'],
  ['minSideDesc', 'bestArea', 'maxArea'],
  ['diffDesc', 'bestArea', 'maxArea'],
  ['areaDesc', 'exactFit', 'shorterAxis'],
  ['maxSideDesc', 'exactFit', 'minArea'],
  ['lengthDesc', 'bestShortSide', 'shorterAxis'],
  ['widthDesc', 'bestShortSide', 'shorterAxis'],
  ['areaDesc', 'bestArea', 'minArea'],
  ['areaDesc', 'bestArea', 'shorterAxis'],
  ['areaDesc', 'bestArea', 'longerAxis'],
  ['perimeterDesc', 'exactFit', 'maxArea'],
  ['areaAsc', 'bestArea', 'maxArea'],
];

/**
 * Eixo do primeiro corte da chapa:
 *  - `preferWidth`  -> corte 'y' (faixas ao longo da largura);
 *  - `preferLength` -> corte 'x';
 *  - `optimal`      -> testa os dois, alem de deixar a regra de corte decidir.
 */
function firstAxesFor(settings: Settings): Array<CutAxis | null> {
  switch (settings.cutOrientation) {
    case 'preferWidth':
      return ['y'];
    case 'preferLength':
      return ['x'];
    default:
      return [null, 'x', 'y'];
  }
}

/** Lista canonica de combinacoes: fase A, depois a varredura sistematica. */
function buildCombos(settings: Settings, instanceCount: number): Combo[] {
  const axes = firstAxesFor(settings);
  const primaryAxis = axes[0] as CutAxis | null;
  const combos: Combo[] = [];
  const seen = new Set<string>();

  const push = (c: Combo): void => {
    const key = `${c.engine}|${c.ordering}|${c.fit}|${c.split}|${c.firstAxis ?? '-'}`;
    if (seen.has(key)) return;
    seen.add(key);
    combos.push(c);
  };

  // --- fase A: prefixo fixo, no eixo preferido -------------------------------
  for (const [ordering, fit, split] of PHASE_A) {
    push({ ordering, fit, split, firstAxis: primaryAxis, engine: 'guillotine' });
  }

  // --- MaxRects: so em problemas pequenos (a reconstrucao e cara) ------------
  if (instanceCount > 0 && instanceCount <= MAXRECTS_LIMIT) {
    for (const ordering of ['areaDesc', 'maxSideDesc', 'perimeterDesc'] as OrderingRule[]) {
      for (const fit of ['bestShortSide', 'bestArea'] as FitRule[]) {
        push({ ordering, fit, split: 'maxArea', firstAxis: null, engine: 'maxrects' });
      }
    }
  }

  // --- fase A nos demais eixos ----------------------------------------------
  for (let a = 1; a < axes.length; a++) {
    for (const [ordering, fit, split] of PHASE_A) {
      push({ ordering, fit, split, firstAxis: axes[a] as CutAxis | null, engine: 'guillotine' });
    }
  }

  // --- fase B: varredura sistematica ----------------------------------------
  for (const axis of axes) {
    for (const ordering of ORDERINGS) {
      for (const fit of FITS) {
        for (const split of SPLITS) {
          push({ ordering, fit, split, firstAxis: axis, engine: 'guillotine' });
        }
      }
    }
  }

  return combos;
}

// -----------------------------------------------------------------------------
// PONTUACAO DA SOLUCAO
// -----------------------------------------------------------------------------

interface RawSheet {
  entry: StockEntry;
  packed: PackedSheet;
}

interface Attempt {
  sheets: RawSheet[];
  leftover: PackItem[];
}

interface Score {
  unplaced: number;
  unplacedArea: Micron2;
  sheets: number;
  stockArea: Micron2;
  usedArea: Micron2;
  wasteArea: Micron2;
  cutLength: number;
  numCuts: number;
  largestOffcut: Micron2;
}

function scoreAttempt(attempt: Attempt, settings: Settings): Score {
  let stockArea = 0;
  let usedArea = 0;
  let cutLength = 0;
  let numCuts = 0;
  let largestOffcut = 0;
  const minTrim = settings.minTrimDimension;

  for (const rs of attempt.sheets) {
    stockArea += rs.entry.area;
    usedArea += rs.packed.usedArea;
    const nodes = rs.packed.nodes;
    for (let i = 0; i < nodes.length; i++) {
      const nd = nodes[i] as (typeof nodes)[number];
      if (nd.kind !== 'split' || nd.axis === null) continue;
      numCuts++;
      cutLength += nd.axis === 'x' ? nd.rect.length : nd.rect.width;
    }
    for (const fr of rs.packed.freeRects) {
      if (fr.width < minTrim || fr.length < minTrim) continue;
      const area = fr.width * fr.length;
      if (area > largestOffcut) largestOffcut = area;
    }
  }

  let unplacedArea = 0;
  for (const it of attempt.leftover) unplacedArea += it.width * it.length;

  return {
    unplaced: attempt.leftover.length,
    unplacedArea,
    sheets: attempt.sheets.length,
    stockArea,
    usedArea,
    wasteArea: stockArea - usedArea,
    cutLength,
    numCuts,
    largestOffcut,
  };
}

/**
 * Vetor lexicografico da funcao objetivo. Pecas nao colocadas SEMPRE dominam:
 * nenhuma economia de chapa compensa deixar uma peca de fora.
 */
function objectiveVector(s: Score, priority: OptimizationPriority): number[] {
  switch (priority) {
    case 'leastCuts':
      return [
        s.unplaced,
        s.unplacedArea,
        s.numCuts,
        s.cutLength,
        s.sheets,
        s.wasteArea,
        -s.largestOffcut,
      ];
    case 'smallerSheets':
      return [
        s.unplaced,
        s.unplacedArea,
        s.stockArea,
        s.sheets,
        s.wasteArea,
        s.cutLength,
        s.numCuts,
      ];
    default:
      return [
        s.unplaced,
        s.unplacedArea,
        s.wasteArea,
        s.sheets,
        -s.largestOffcut,
        s.cutLength,
        s.numCuts,
      ];
  }
}

/** -1 = `a` melhor · 0 = empate · 1 = `b` melhor. Tolerancia relativa. */
function compareScore(a: Score, b: Score, priority: OptimizationPriority): number {
  const va = objectiveVector(a, priority);
  const vb = objectiveVector(b, priority);
  for (let i = 0; i < va.length; i++) {
    const x = va[i] as number;
    const y = vb[i] as number;
    const diff = x - y;
    const tol = 1e-9 * Math.max(1, Math.abs(x), Math.abs(y));
    if (Math.abs(diff) > tol) return diff < 0 ? -1 : 1;
  }
  return 0;
}

function emptyScore(): Score {
  return {
    unplaced: 0,
    unplacedArea: 0,
    sheets: 0,
    stockArea: 0,
    usedArea: 0,
    wasteArea: 0,
    cutLength: 0,
    numCuts: 0,
    largestOffcut: 0,
  };
}

// -----------------------------------------------------------------------------
// UMA TENTATIVA COMPLETA
// -----------------------------------------------------------------------------

interface Guard {
  /** Instante (nowMs) a partir do qual a busca deve parar. */
  deadline: number;
  stop: () => boolean;
  /** Trabalho ja gasto, em unidades DETERMINISTICAS (pecas varridas). */
  work: number;
  /** Teto de trabalho derivado de `timeBudgetMs`. Este e o corte principal. */
  workBudget: number;
}

/** true quando o orcamento de tempo acabou (valvula de seguranca, nao contrato). */
function deadlineReached(guard: Guard): boolean {
  return nowMs() >= guard.deadline;
}

/** true quando o orcamento de TRABALHO acabou — deterministico, nao usa relogio. */
function workExhausted(guard: Guard): boolean {
  return guard.work >= guard.workBudget;
}

interface SheetAttempt {
  entryIndex: number;
  packed: PackedSheet;
  leftover: PackItem[];
}

/** `'abort'` = layout MaxRects nao guilhotinavel; a tentativa inteira e descartada. */
type ChoiceResult = SheetAttempt | null | 'abort';

/** Empacota uma chapa com o motor da combinacao. */
function packOnSheet(
  sheet: StockSheet,
  items: readonly PackItem[],
  settings: Settings,
  combo: Combo,
): { packed: PackedSheet; leftover: PackItem[] } | null {
  if (combo.engine === 'maxrects') {
    const res = packSheetMaxRects(sheet, items, settings, { fit: combo.fit });
    if (res.placements.length === 0) {
      return {
        packed: { nodes: [], placements: [], freeRects: [], usedArea: 0 },
        leftover: res.leftover,
      };
    }
    const region: Rect = { x: 0, y: 0, width: sheet.width, length: sheet.length };
    const nodes = reconstructGuillotine(region, res.placements, settings.kerf);
    // O clone e 100% guilhotinado: layout que a serra nao consegue produzir
    // e simplesmente recusado.
    if (nodes === null) return null;
    return {
      packed: {
        nodes,
        placements: res.placements,
        freeRects: collectFreeRects(nodes),
        usedArea: res.usedArea,
      },
      leftover: res.leftover,
    };
  }

  return packSheetGuillotine(sheet, items, settings, {
    fit: combo.fit,
    split: combo.split,
    firstAxis: combo.firstAxis,
  });
}

/**
 * Escolhe a proxima chapa do estoque e ja devolve o empacotamento dela.
 * Com poucos tipos de chapa faz um empacotamento de TESTE com cada um e fica
 * com o de melhor aproveitamento por area — o teste vencedor e reaproveitado,
 * entao o custo extra e so o dos perdedores.
 */
function chooseAndPack(
  pool: readonly StockEntry[],
  blocked: Set<number>,
  remaining: readonly PackItem[],
  settings: Settings,
  combo: Combo,
  guard: Guard,
): ChoiceResult {
  const candidates: number[] = [];
  for (let i = 0; i < pool.length; i++) {
    if (blocked.has(i)) continue;
    if ((pool[i] as StockEntry).remaining <= 0) continue;
    candidates.push(i);
  }
  if (candidates.length === 0) return null;

  const smallerFirst = settings.priority === 'smallerSheets';
  candidates.sort((a, b) => {
    const ea = pool[a] as StockEntry;
    const eb = pool[b] as StockEntry;
    if (ea.area !== eb.area) return smallerFirst ? ea.area - eb.area : eb.area - ea.area;
    return ea.order - eb.order;
  });

  const useTrial =
    combo.engine === 'guillotine' &&
    candidates.length > 1 &&
    candidates.length <= 4 &&
    remaining.length <= 250;

  // Cada empacotamento de teste varre a lista de pecas que sobrou: e essa a
  // unidade de trabalho cobrada do orcamento (ver WORK_UNITS_PER_MS).
  const charge = (): void => {
    guard.work += remaining.length + 1;
  };

  if (!useTrial) {
    for (const idx of candidates) {
      const entry = pool[idx] as StockEntry;
      charge();
      const res = packOnSheet(entry.sheet, remaining, settings, combo);
      if (res === null) return 'abort';
      if (res.packed.placements.length === 0) {
        blocked.add(idx);
        continue;
      }
      return { entryIndex: idx, packed: res.packed, leftover: res.leftover };
    }
    return null;
  }

  let best: SheetAttempt | null = null;
  let bestValue = -1;
  for (const idx of candidates) {
    const entry = pool[idx] as StockEntry;
    charge();
    const res = packOnSheet(entry.sheet, remaining, settings, combo);
    if (res === null) return 'abort';
    if (res.packed.placements.length === 0) {
      blocked.add(idx);
      continue;
    }
    const value = entry.area > 0 ? res.packed.usedArea / entry.area : 0;
    if (value > bestValue) {
      bestValue = value;
      best = { entryIndex: idx, packed: res.packed, leftover: res.leftover };
    }
  }
  return best;
}

/** Empacota TODAS as pecas da ordem dada, abrindo chapas enquanto houver estoque. */
function runAttempt(
  order: readonly PackItem[],
  pool: StockEntry[],
  settings: Settings,
  combo: Combo,
  maxSheets: number,
  guard: Guard,
  cutByWork: boolean,
  cutByClock: boolean,
): Attempt | null {
  resetStockPool(pool);
  let remaining: PackItem[] = order.slice();
  const sheets: RawSheet[] = [];
  const blocked = new Set<number>();

  while (remaining.length > 0 && sheets.length < maxSheets) {
    // Cancelamento do usuario e sempre respeitado. O corte por TRABALHO
    // (deterministico) vale para toda tentativa que nao seja a primeira; o
    // relogio so interrompe tentativas fora do nucleo fixo.
    if (guard.stop()) return null;
    if (cutByWork && workExhausted(guard)) return null;
    if (cutByClock && deadlineReached(guard)) return null;
    const choice = chooseAndPack(pool, blocked, remaining, settings, combo, guard);
    if (choice === 'abort') return null;
    if (choice === null) break;
    const entry = pool[choice.entryIndex] as StockEntry;
    entry.remaining -= 1;
    sheets.push({ entry, packed: choice.packed });
    remaining = choice.leftover;
  }

  return { sheets, leftover: remaining };
}

// -----------------------------------------------------------------------------
// BUSCA POR MATERIAL
// -----------------------------------------------------------------------------

interface MaterialResult {
  sheets: RawSheet[];
  leftover: PackItem[];
  score: Score;
  /** Numero de chapas que o orcamento permitia — detecta truncamento. */
  sheetCap: number;
}

/** Copia ordenada segundo a regra, com cache por regra (no maximo 8 ordenacoes). */
function orderedItems(
  rule: OrderingRule,
  items: readonly PackItem[],
  cache: Map<OrderingRule, PackItem[]>,
): PackItem[] {
  const hit = cache.get(rule);
  if (hit !== undefined) return hit;
  const arr = items.slice();
  arr.sort((a, b) => compareItems(rule, a, b));
  cache.set(rule, arr);
  return arr;
}

/**
 * Perturbacao da melhor ordem conhecida (fase C).
 * Alterna entre trocas aleatorias de pares e embaralhamento de uma janela.
 * Custo O(n) — nada de GRASP com `splice`, que estouraria com 2000 pecas.
 */
function perturbOrder(base: readonly PackItem[], rng: Rng, attempt: number): PackItem[] {
  const out = base.slice();
  const n = out.length;
  if (n < 2) return out;

  if (attempt % 2 === 0) {
    const alphas = [0.02, 0.05, 0.12];
    const alpha = alphas[Math.floor(attempt / 2) % alphas.length] as number;
    const swaps = Math.max(1, Math.ceil(n * alpha));
    for (let s = 0; s < swaps; s++) {
      const i = randInt(rng, n);
      const j = randInt(rng, n);
      const tmp = out[i] as PackItem;
      out[i] = out[j] as PackItem;
      out[j] = tmp;
    }
    return out;
  }

  const maxWindow = Math.max(2, Math.ceil(n / 5));
  const len = Math.min(n, 2 + randInt(rng, maxWindow - 1));
  const start = randInt(rng, n - len + 1);
  const window = out.slice(start, start + len);
  shuffle(window, rng);
  for (let k = 0; k < window.length; k++) out[start + k] = window[k] as PackItem;
  return out;
}

/** Limite inferior de chapas: area total das pecas / maior area util disponivel. */
function lowerBoundSheets(items: readonly PackItem[], pool: readonly StockEntry[]): number {
  let area = 0;
  for (const it of items) area += it.width * it.length;
  let maxArea = 0;
  for (const e of pool) if (e.area > maxArea) maxArea = e.area;
  if (maxArea <= 0 || area <= 0) return 0;
  return Math.max(1, Math.ceil(area / maxArea));
}

function poolIsUniform(pool: readonly StockEntry[]): boolean {
  if (pool.length <= 1) return true;
  const first = (pool[0] as StockEntry).area;
  for (const e of pool) if (e.area !== first) return false;
  return true;
}

/**
 * Quantas tentativas cabem — derivado do ORCAMENTO e do TAMANHO do problema,
 * de forma deterministica. O relogio e so valvula de seguranca: assim, em
 * qualquer maquina razoavel, a mesma entrada percorre as mesmas tentativas.
 * A constante equivale a ~60 mil pecas empacotadas por segundo.
 */
export function attemptsFor(budgetMs: number, instances: number): number {
  const n = Math.max(1, instances);
  const raw = Math.floor((budgetMs / 1000) * (60_000 / n));
  return Math.max(4, Math.min(240, raw));
}

function searchMaterial(
  key: string,
  items: readonly PackItem[],
  pool: StockEntry[],
  settings: Settings,
  combos: readonly Combo[],
  attempts: number,
  maxSheets: number,
  guard: Guard,
  onIteration: (best: MaterialResult | null, done: number, total: number) => void,
): MaterialResult | null {
  if (items.length === 0) {
    return { sheets: [], leftover: [], score: emptyScore(), sheetCap: maxSheets };
  }
  if (combos.length === 0 || maxSheets <= 0) {
    return { sheets: [], leftover: items.slice(), score: emptyScore(), sheetCap: maxSheets };
  }

  const cache = new Map<OrderingRule, PackItem[]>();
  const lb = lowerBoundSheets(items, pool);
  const uniform = poolIsUniform(pool);

  let best: MaterialResult | null = null;
  let bestCombo = combos[0] as Combo;
  let bestOrder: PackItem[] | null = null;

  for (let i = 0; i < attempts; i++) {
    // A PRIMEIRA tentativa roda sempre inteira: e ela que garante um plano
    // valido, custe o que custar. Da segunda em diante o corte e o orcamento de
    // TRABALHO — deterministico, entao o plano nao depende da maquina. Fora do
    // nucleo fixo (fase A) o relogio entra como valvula de seguranca extra.
    // Aumentar o orcamento so pode manter ou melhorar o plano, nunca piorar:
    // mais trabalho permitido = mais tentativas sobre o mesmo prefixo.
    const first = i === 0;
    const beyondCore = i >= PHASE_A.length;
    if (guard.stop()) break;
    if (!first && workExhausted(guard)) break;
    if (beyondCore && deadlineReached(guard)) break;

    let combo: Combo;
    let order: PackItem[];
    if (i < combos.length) {
      combo = combos[i] as Combo;
      order = orderedItems(combo.ordering, items, cache);
    } else {
      combo = bestCombo;
      const rng = mulberry32(hash32(settings.seed, key, i, combo.ordering, combo.fit));
      const base = bestOrder ?? orderedItems(combo.ordering, items, cache);
      order = perturbOrder(base, rng, i);
    }

    const attempt = runAttempt(order, pool, settings, combo, maxSheets, guard, !first, beyondCore);
    if (attempt !== null) {
      const score = scoreAttempt(attempt, settings);
      if (best === null || compareScore(score, best.score, settings.priority) < 0) {
        best = {
          sheets: attempt.sheets,
          leftover: attempt.leftover,
          score,
          sheetCap: maxSheets,
        };
        bestCombo = combo;
        bestOrder = order;
      }
    }

    onIteration(best, i + 1, attempts);

    // Parada antecipada: so DEPOIS do nucleo fixo (fase A), para que os
    // criterios secundarios (cortes, maior sobra) tambem sejam explorados.
    if (
      i + 1 >= PHASE_A.length &&
      best !== null &&
      best.leftover.length === 0 &&
      uniform &&
      best.sheets.length <= lb
    ) {
      break;
    }
  }

  return best;
}

// -----------------------------------------------------------------------------
// PRE-FILTRO DE ENCAIXE
// -----------------------------------------------------------------------------

function fitsAnySheet(
  item: PackItem,
  sheets: readonly StockSheet[],
  considerGrain: boolean,
): boolean {
  for (const s of sheets) {
    for (const o of orientationsFor(item, s, considerGrain)) {
      if (o.width <= s.width && o.length <= s.length) return true;
    }
  }
  return false;
}

function fitsAnySheetIgnoringGrain(item: PackItem, sheets: readonly StockSheet[]): boolean {
  for (const s of sheets) {
    if (item.width <= s.width && item.length <= s.length) return true;
    if (item.length <= s.width && item.width <= s.length) return true;
  }
  return false;
}

// -----------------------------------------------------------------------------
// NORMALIZACAO DEFENSIVA DA ENTRADA
// -----------------------------------------------------------------------------

function normalizePart(p: Part): Part {
  const eb = p.edgeBanding;
  const safeBanding =
    eb !== null && eb !== undefined && typeof eb === 'object'
      ? {
          top: eb.top ?? null,
          right: eb.right ?? null,
          bottom: eb.bottom ?? null,
          left: eb.left ?? null,
        }
      : { top: null, right: null, bottom: null, left: null };
  return {
    ...p,
    id: typeof p.id === 'string' && p.id !== '' ? p.id : 'peca',
    label: typeof p.label === 'string' ? p.label : '',
    material: typeof p.material === 'string' ? p.material : '',
    color: typeof p.color === 'string' && p.color !== '' ? p.color : '#CFD6DD',
    orientation: p.orientation === 1 || p.orientation === 2 ? p.orientation : 0,
    enabled: p.enabled !== false,
    edgeBanding: safeBanding,
  };
}

function normalizeStock(s: StockSheet): StockSheet {
  return {
    ...s,
    id: typeof s.id === 'string' && s.id !== '' ? s.id : 'chapa',
    label: typeof s.label === 'string' ? s.label : '',
    material: typeof s.material === 'string' ? s.material : '',
    orientation: s.orientation === 1 || s.orientation === 2 ? s.orientation : 0,
    enabled: s.enabled !== false,
    qty: s.qty === null || s.qty === undefined ? null : s.qty,
  };
}

// -----------------------------------------------------------------------------
// API PUBLICA
// -----------------------------------------------------------------------------

/**
 * Calcula o plano de corte.
 *
 * Nunca lanca: entrada invalida vira `UnfitPart` com `reason: 'invalid'` e/ou
 * mensagens em `warnings`. Para o assim que `shouldStop()` devolver `true`,
 * devolvendo a MELHOR solucao encontrada ate ali.
 */
export function optimize(
  input: OptimizationInput,
  onProgress?: (e: ProgressEvent) => void,
  shouldStop?: () => boolean,
): OptimizationResult {
  const started = nowMs();
  try {
    return runOptimize(input, started, onProgress, shouldStop);
  } catch (err) {
    const raw = input as OptimizationInput | undefined | null;
    const settings = normalizeSettings(raw ? raw.settings : null);
    const detail = err instanceof Error ? err.message : String(err);
    return {
      sheets: [],
      stats: computeStats([], [], [], [], settings, Math.max(0, nowMs() - started)),
      unfit: [],
      warnings: [`Falha inesperada no cálculo do plano de corte: ${detail}`],
    };
  }
}

interface UnfitAcc {
  part: Part;
  reason: UnfitReason;
  qty: number;
  message?: string;
}

/** Peca sintetica para o caso (teoricamente impossivel) de id orfao. */
function fallbackPart(it: PackItem): Part {
  return {
    id: it.partId,
    length: it.length,
    width: it.width,
    qty: 0,
    label: it.label,
    material: it.material,
    orientation: it.orientation,
    enabled: true,
    edgeBanding: it.edgeBanding,
    color: it.color,
  };
}

function runOptimize(
  input: OptimizationInput,
  started: number,
  onProgress?: (e: ProgressEvent) => void,
  shouldStop?: () => boolean,
): OptimizationResult {
  const raw = input as OptimizationInput | undefined | null;
  const safeInput = (raw ? raw : {}) as Partial<OptimizationInput>;
  const settings = normalizeSettings(safeInput.settings ?? null);
  const parts: Part[] = (Array.isArray(safeInput.parts) ? safeInput.parts : [])
    .filter((p): p is Part => p !== null && p !== undefined && typeof p === 'object')
    .map(normalizePart);
  const stock: StockSheet[] = (Array.isArray(safeInput.stock) ? safeInput.stock : [])
    .filter((s): s is StockSheet => s !== null && s !== undefined && typeof s === 'object')
    .map(normalizeStock);
  const edgeBands = Array.isArray(safeInput.edgeBands) ? safeInput.edgeBands : [];

  const warnings: string[] = [];

  const stopFn = (): boolean => {
    if (typeof shouldStop !== 'function') return false;
    try {
      return shouldStop() === true;
    } catch {
      return false;
    }
  };

  // --- progresso -------------------------------------------------------------
  let lastEmit = Number.NEGATIVE_INFINITY;
  let lastPercent = 0;
  let iterations = 0;
  let bestSummary: ProgressEvent['best'] = null;

  const emit = (phase: OptimizationPhase, percent: number, force: boolean): void => {
    if (typeof onProgress !== 'function') return;
    const t = nowMs();
    if (!force && t - lastEmit < PROGRESS_INTERVAL_MS) return;
    lastEmit = t;
    if (percent > lastPercent) lastPercent = percent;
    if (lastPercent > 100) lastPercent = 100;
    try {
      onProgress({
        phase,
        percent: lastPercent,
        elapsedMs: Math.max(0, t - started),
        iterations,
        best: bestSummary,
      });
    } catch {
      // Um callback com defeito nunca pode derrubar o calculo.
    }
  };

  emit('validate', 0, true);

  // --- pecas que nao couberam ------------------------------------------------
  const unfitAcc = new Map<string, UnfitAcc>();
  const unfitOrder: string[] = [];
  /**
   * `sourceKey` identifica a LINHA da tabela, nunca o `id` — dois cadastros
   * podem repetir o mesmo id e ainda assim ter rotulo e medidas diferentes.
   */
  const addUnfit = (
    part: Part,
    sourceKey: string,
    reason: UnfitReason,
    qty: number,
    message?: string,
  ): void => {
    if (qty <= 0) return;
    const key = `${sourceKey}|${reason}`;
    const cur = unfitAcc.get(key);
    if (cur !== undefined) {
      cur.qty += qty;
      return;
    }
    unfitAcc.set(key, { part, reason, qty, message });
    unfitOrder.push(key);
  };

  // --- expansao das pecas ----------------------------------------------------
  // INDICE INTERNO: cada copia sabe de qual LINHA de `parts` ela saiu. Nao da
  // para voltar pelo `id`, porque o usuario pode repetir ids — foi assim que a
  // lista de "nao couberam" ja mostrou o rotulo de uma peca com as medidas de
  // outra. O mapa e por identidade do objeto `PackItem`, que os empacotadores
  // devolvem intacto em `leftover`.
  const bands = bandThicknessMap(edgeBands);
  const itemSource = new Map<PackItem, number>();
  const items: PackItem[] = [];
  let requested = 0;

  for (let pi = 0; pi < parts.length; pi++) {
    const part = parts[pi] as Part;
    if (!part.enabled) continue;
    const prep = preparePart(part, settings, bands);
    if (prep.qty <= 0) continue;
    requested += prep.qty;
    if (prep.invalid) {
      addUnfit(part, `#${pi}`, 'invalid', prep.qty, prep.message);
      continue;
    }
    for (let i = 0; i < prep.qty; i++) {
      const item: PackItem = {
        partId: part.id,
        instance: i,
        label: part.label,
        color: part.color,
        material: part.material,
        width: prep.cutWidth,
        length: prep.cutLength,
        orientation: part.orientation,
        edgeBanding: part.edgeBanding,
      };
      itemSource.set(item, pi);
      items.push(item);
    }
  }

  /** Contabiliza uma copia que ficou de fora, sempre pela linha de origem. */
  const addUnfitItem = (it: PackItem, reason: UnfitReason): void => {
    const pi = itemSource.get(it);
    const part = pi !== undefined ? (parts[pi] as Part) : fallbackPart(it);
    addUnfit(part, pi !== undefined ? `#${pi}` : `?${it.partId}`, reason, 1);
  };

  const pool = buildStockPool(stock, settings);
  emit('prepare', 5, true);

  if (items.length > 0 && pool.length === 0) {
    warnings.push('Nenhuma chapa de estoque ativa e válida foi encontrada.');
  }

  // --- separacao por material ------------------------------------------------
  const itemsByMaterial = groupByMaterial(items, settings.considerMaterial);
  const stockWrapped = pool.map((entry) => ({ material: entry.sheet.material, entry }));
  const stockGroups = groupByMaterial(stockWrapped, settings.considerMaterial);
  const materialKeys = Array.from(itemsByMaterial.keys()).sort();

  const totalInstances = items.length;
  const solutions: RawSheet[] = [];
  // "Usar apenas uma chapa do estoque" e um teto GLOBAL: exatamente UMA unidade
  // fisica no total, nao uma por linha de estoque. O pool continua oferecendo
  // uma chapa de cada linha (para a busca escolher a melhor), mas o orcamento
  // global de chapas garante que so uma sera de fato aberta.
  const globalSheetCap = settings.useSingleStockUnit ? 1 : MAX_SHEETS;
  let sheetBudget = globalSheetCap;
  let truncated = false;

  emit('search', 10, true);

  for (let m = 0; m < materialKeys.length; m++) {
    const key = materialKeys[m] as string;
    const groupItems = itemsByMaterial.get(key) ?? [];
    const groupStock = stockGroups.get(key) ?? [];
    const groupPool = groupStock.map((g) => g.entry);

    if (groupPool.length === 0) {
      for (const it of groupItems) addUnfitItem(it, 'noMaterialStock');
      continue;
    }

    // --- pre-filtro: peca grande demais / travada pelo veio -------------------
    const sheetsOfGroup = groupPool.map((e) => e.sheet);
    const verdicts = new Map<string, number>();
    const keep: PackItem[] = [];
    for (const it of groupItems) {
      const classKey = `${it.width}x${it.length}|${it.orientation}`;
      let verdict = verdicts.get(classKey);
      if (verdict === undefined) {
        verdict = fitsAnySheet(it, sheetsOfGroup, settings.considerGrain)
          ? 0
          : fitsAnySheetIgnoringGrain(it, sheetsOfGroup)
            ? 1
            : 2;
        verdicts.set(classKey, verdict);
      }
      if (verdict === 0) keep.push(it);
      else addUnfitItem(it, verdict === 1 ? 'grainConflict' : 'tooLarge');
    }

    if (keep.length === 0) continue;

    // --- orcamento deste material --------------------------------------------
    const share = totalInstances > 0 ? keep.length / totalInstances : 1;
    // Piso para que um material pequeno ainda tenha chance de busca — mas o
    // piso NUNCA pode passar do orcamento total pedido pelo usuario.
    const floor = Math.min(200, settings.timeBudgetMs);
    const materialBudget = Math.max(floor, settings.timeBudgetMs * share);
    const attempts = attemptsFor(materialBudget, keep.length);
    const combos = buildCombos(settings, keep.length);
    const guard: Guard = {
      deadline: nowMs() + materialBudget,
      stop: stopFn,
      work: 0,
      workBudget: materialBudget * WORK_UNITS_PER_MS,
    };
    const doneBefore = iterations;
    const sheetsBefore = solutions.length;

    const result = searchMaterial(
      key,
      keep,
      groupPool,
      settings,
      combos,
      attempts,
      sheetBudget,
      guard,
      (best, done, total) => {
        iterations = doneBefore + done;
        if (best !== null) {
          const area = best.score.stockArea;
          bestSummary = {
            numSheets: sheetsBefore + best.sheets.length,
            usedAreaRatio: area > 0 ? best.score.usedArea / area : 0,
            numUnfitParts: best.leftover.length,
          };
        }
        const inner = total > 0 ? done / total : 1;
        emit('search', 10 + (85 * (m + inner)) / materialKeys.length, false);
      },
    );

    if (result === null) {
      for (const it of keep) addUnfitItem(it, 'stockExhausted');
      continue;
    }

    for (const rs of result.sheets) solutions.push(rs);
    for (const it of result.leftover) addUnfitItem(it, 'stockExhausted');

    sheetBudget = Math.max(0, sheetBudget - result.sheets.length);
    // No modo "uma chapa so" ficar sem chapa e o comportamento pedido, nao um
    // truncamento por limite interno — as pecas de fora ja viram `unfit`.
    if (
      !settings.useSingleStockUnit &&
      result.leftover.length > 0 &&
      result.sheets.length >= result.sheetCap
    ) {
      truncated = true;
    }
  }

  if (truncated) {
    warnings.push(
      `O plano chegou ao limite de ${MAX_SHEETS.toLocaleString('pt-BR')} chapas. As peças restantes ficaram de fora.`,
    );
  }

  // --- montagem das chapas ---------------------------------------------------
  emit('finalize', 95, true);

  const layouts: SheetLayout[] = [];
  for (let i = 0; i < solutions.length; i++) {
    const rs = solutions[i] as RawSheet;
    const sheet = rs.entry.sheet;
    const cuts = extractCuts(rs.packed, i, settings);
    let cutLength = 0;
    for (const c of cuts) cutLength += c.length;
    const area = sheet.width * sheet.length;
    layouts.push({
      index: i,
      stockSheetId: sheet.id,
      label: sheet.label,
      material: sheet.material,
      width: sheet.width,
      length: sheet.length,
      repeat: 1,
      placements: rs.packed.placements,
      offcuts: collectOffcuts(rs.packed.freeRects, settings.minTrimDimension, i),
      cuts,
      usedArea: rs.packed.usedArea,
      wasteArea: area - rs.packed.usedArea,
      usedAreaRatio: area > 0 ? rs.packed.usedArea / area : 0,
      cutLength,
      numCuts: cuts.length,
    });
  }

  const stacked = stackIdenticalSheets(layouts, settings.stackSamePanels);
  const sheets: SheetLayout[] = stacked.map((s, i) => ({
    ...s,
    index: i,
    cuts: s.cuts.map((c) => ({ ...c, sheetIndex: i })),
    offcuts: s.offcuts.map((o, j) => ({ ...o, id: `off-${i}-${j}` })),
  }));

  // --- pecas de fora ---------------------------------------------------------
  const unfitList: UnfitPart[] = unfitOrder.map((key) => {
    const acc = unfitAcc.get(key) as UnfitAcc;
    return makeUnfit(acc.part, acc.qty, acc.reason, settings, acc.message);
  });
  const unfit = mergeUnfit(unfitList);

  // --- conferencia: colocadas + de fora === pedidas --------------------------
  let placed = 0;
  for (const s of sheets) placed += s.placements.length * (s.repeat > 0 ? s.repeat : 1);
  let outCount = 0;
  for (const u of unfit) outCount += u.qty;
  if (placed + outCount !== requested) {
    warnings.push(
      `Conferência interna: ${placed + outCount} peças contabilizadas para ${requested} pedidas.`,
    );
  }

  const stats = computeStats(
    sheets,
    unfit,
    parts,
    edgeBands,
    settings,
    Math.max(0, nowMs() - started),
  );

  emit('finalize', 100, true);

  return { sheets, stats, unfit, warnings };
}

export type { Combo, OrderingRule };
